<app-header [breadcrumbs]="['System settings', 'API']"
            [links]="['/settings/general-information', '/settings/api']"
            mainHeading="API" />

<form (ngSubmit)="createToken()">
    <div class="settings-cards-layout">
        <div class="settings-cards-span">
            <cll-card appearance="outlined">
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">api</cll-icon>
                    <cll-card-title>API access</cll-card-title>
                </cll-card-header>

                <div class="panel-text-sub-header">
                    Create an API token for external reporting integrations.
                </div>

                <cll-form-field label="Token name" class="w-full">
                    <input cllFormInput [formControl]="createTokenFormGroup.controls.name" type="text">
                </cll-form-field>

                <cll-card-footer>
                    <button cll-filled-button colour="secondary" type="submit">
                        <cll-icon cllButtonIcon>check</cll-icon>
                        Create token
                    </button>
                </cll-card-footer>
            </cll-card>

            <cll-card appearance="outlined">
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">play_circle</cll-icon>
                    <cll-card-title>Live preview</cll-card-title>
                </cll-card-header>

                <div class="panel-text-sub-header">
                    Paste an API token to run live preview requests directly from any URL builder below.
                    @if (createdToken) { Your newly created token is used automatically if this field is left blank. }
                </div>

                <cll-form-field label="API token" class="w-full">
                    <input cllFormInput type="password" [(ngModel)]="previewToken" [ngModelOptions]="{standalone: true}"
                           [placeholder]="createdToken ? 'Leave blank to use newly created token' : 'Paste your API token'">
                </cll-form-field>
            </cll-card>

            <cll-collapsible-card>
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">link</cll-icon>
                    <cll-card-title>Providers URL builder</cll-card-title>
                </cll-card-header>

                <div class="panel-text-sub-header">
                    Choose filters and pagination options to generate a ready-to-use providers API URL.
                </div>

                <div class="url-builder-grid">
                    <cll-form-field label="Name contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="builderName" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Reference contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="builderReference" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Email contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="builderEmail" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Status" class="w-full">
                        <cll-select
                            [options]="statusDataSource"
                            [formControl]="builderStatusControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Search (q)" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="builderQuery" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Roles" class="w-full">
                        <cll-multi-select
                            [options]="builderRoleDataSource"
                            [formControl]="builderRolesControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Columns" class="w-full">
                        <cll-multi-select
                            [options]="builderColumnDataSource"
                            [formControl]="builderColumnsControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort field" class="w-full">
                        <cll-select
                            [options]="sortFieldDataSource"
                            [formControl]="builderSortControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort direction" class="w-full">
                        <cll-select
                            [options]="sortDirectionDataSource"
                            [formControl]="builderSortDirectionControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Per page (1-500)" class="w-full">
                        <input cllFormInput type="number" [(ngModel)]="builderPerPage" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>
                </div>

                <div class="url-builder-includes">
                    <app-toggle
                        [checked]="builderIncludeRegions"
                        (toggled)="builderIncludeRegions = $event"
                        label="Include regions" />
                    <app-toggle
                        [checked]="builderIncludeZones"
                        (toggled)="builderIncludeZones = $event"
                        label="Include zones" />
                    <app-toggle
                        [checked]="builderIncludePriorities"
                        (toggled)="builderIncludePriorities = $event"
                        label="Include priorities" />
                </div>

                <div class="url-builder-grid">
                    @if (builderIncludeRegions) {
                        <cll-form-field label="Region columns" class="w-full">
                            <cll-multi-select
                                [options]="builderRegionColumnDataSource"
                                [formControl]="builderRegionColumnsControl"
                            />
                        </cll-form-field>
                    }

                    @if (builderIncludeZones) {
                        <cll-form-field label="Zone columns" class="w-full">
                            <cll-multi-select
                                [options]="builderZoneColumnDataSource"
                                [formControl]="builderZoneColumnsControl"
                            />
                        </cll-form-field>
                    }

                    @if (builderIncludePriorities) {
                        <cll-form-field label="Priority columns" class="w-full">
                            <cll-multi-select
                                [options]="builderPriorityColumnDataSource"
                                [formControl]="builderPriorityColumnsControl"
                            />
                        </cll-form-field>
                    }
                </div>

                <cll-form-field label="Generated URL" class="w-full">
                    <textarea cllFormInput [value]="providersUrl" rows="4" readonly></textarea>
                </cll-form-field>

                @if (previewResults['providers']; as result) {
                    <div class="api-preview">
                        @if (result.loading) {
                            <span class="api-preview-loading">Running…</span>
                        } @else {
                            <div class="api-preview-meta">
                                <span class="api-preview-status" [class.ok]="(result.status ?? 0) < 400">HTTP {{ result.status }}</span>
                                <span class="api-preview-duration">{{ result.duration }}ms</span>
                            </div>
                            @if (result.error) {
                                <div class="api-preview-error">{{ result.error }}</div>
                            } @else {
                                <pre class="api-preview-body">{{ result.body }}</pre>
                            }
                        }
                    </div>
                }

                <cll-card-footer>
                    <button cll-outline-button type="button" (click)="copyProvidersUrl()">
                        <cll-icon cllButtonIcon type="outlined">content_copy</cll-icon>
                        Copy URL
                    </button>
                    <button cll-outline-button type="button" (click)="runPreview('providers', providersUrl)" [disabled]="previewResults['providers']?.loading">
                        <cll-icon cllButtonIcon type="outlined">play_arrow</cll-icon>
                        Preview
                    </button>
                </cll-card-footer>
            </cll-collapsible-card>

            <cll-collapsible-card>
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">security</cll-icon>
                    <cll-card-title>Permissions URL builder</cll-card-title>
                </cll-card-header>

                <div class="panel-text-sub-header">
                    Build a roles/permission-types API URL for commissioner, provider, and admin role data.
                </div>

                <div class="url-builder-grid">
                    <cll-form-field label="Name contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="permissionBuilderName" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Key contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="permissionBuilderKey" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Search (q)" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="permissionBuilderQuery" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>
                </div>

                <cll-form-field label="Generated URL" class="w-full">
                    <textarea cllFormInput [value]="permissionTypesUrl" rows="3" readonly></textarea>
                </cll-form-field>

                @if (previewResults['permissions']; as result) {
                    <div class="api-preview">
                        @if (result.loading) {
                            <span class="api-preview-loading">Running…</span>
                        } @else {
                            <div class="api-preview-meta">
                                <span class="api-preview-status" [class.ok]="(result.status ?? 0) < 400">HTTP {{ result.status }}</span>
                                <span class="api-preview-duration">{{ result.duration }}ms</span>
                            </div>
                            @if (result.error) {
                                <div class="api-preview-error">{{ result.error }}</div>
                            } @else {
                                <pre class="api-preview-body">{{ result.body }}</pre>
                            }
                        }
                    </div>
                }

                <cll-card-footer>
                    <button cll-outline-button type="button" (click)="copyPermissionTypesUrl()">
                        <cll-icon cllButtonIcon type="outlined">content_copy</cll-icon>
                        Copy URL
                    </button>
                    <button cll-outline-button type="button" (click)="runPreview('permissions', permissionTypesUrl)" [disabled]="previewResults['permissions']?.loading">
                        <cll-icon cllButtonIcon type="outlined">play_arrow</cll-icon>
                        Preview
                    </button>
                </cll-card-footer>
            </cll-collapsible-card>

            <cll-collapsible-card>
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">group</cll-icon>
                    <cll-card-title>Users URL builder</cll-card-title>
                </cll-card-header>

                <div class="panel-text-sub-header">
                    Build a users API URL with filters for role, status, search, sort, and selected columns.
                </div>

                <div class="url-builder-grid">
                    <cll-form-field label="Reference contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="usersBuilderReference" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Email contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="usersBuilderEmail" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Status" class="w-full">
                        <cll-select
                            [options]="statusDataSource"
                            [formControl]="usersBuilderStatusControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Roles" class="w-full">
                        <cll-multi-select
                            [options]="builderRoleDataSource"
                            [formControl]="usersBuilderRolesControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Search (q)" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="usersBuilderQuery" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Columns" class="w-full">
                        <cll-multi-select
                            [options]="usersBuilderColumnDataSource"
                            [formControl]="usersBuilderColumnsControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort field" class="w-full">
                        <cll-select
                            [options]="usersBuilderColumnDataSource"
                            [formControl]="usersBuilderSortControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort direction" class="w-full">
                        <cll-select
                            [options]="sortDirectionDataSource"
                            [formControl]="usersBuilderSortDirectionControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Per page (1-500)" class="w-full">
                        <input cllFormInput type="number" [(ngModel)]="usersBuilderPerPage" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>
                </div>

                <cll-form-field label="Generated URL" class="w-full">
                    <textarea cllFormInput [value]="usersUrl" rows="3" readonly></textarea>
                </cll-form-field>

                @if (previewResults['users']; as result) {
                    <div class="api-preview">
                        @if (result.loading) {
                            <span class="api-preview-loading">Running…</span>
                        } @else {
                            <div class="api-preview-meta">
                                <span class="api-preview-status" [class.ok]="(result.status ?? 0) < 400">HTTP {{ result.status }}</span>
                                <span class="api-preview-duration">{{ result.duration }}ms</span>
                            </div>
                            @if (result.error) {
                                <div class="api-preview-error">{{ result.error }}</div>
                            } @else {
                                <pre class="api-preview-body">{{ result.body }}</pre>
                            }
                        }
                    </div>
                }

                <cll-card-footer>
                    <button cll-outline-button type="button" (click)="copyUsersUrl()">
                        <cll-icon cllButtonIcon type="outlined">content_copy</cll-icon>
                        Copy URL
                    </button>
                    <button cll-outline-button type="button" (click)="runPreview('users', usersUrl)" [disabled]="previewResults['users']?.loading">
                        <cll-icon cllButtonIcon type="outlined">play_arrow</cll-icon>
                        Preview
                    </button>
                </cll-card-footer>
            </cll-collapsible-card>

            <cll-collapsible-card>
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">badge</cll-icon>
                    <cll-card-title>Commissioners URL builder</cll-card-title>
                </cll-card-header>

                <div class="panel-text-sub-header">
                    Build a commissioners API URL with filter, include, sort, and selected column options.
                </div>

                <div class="url-builder-grid">
                    <cll-form-field label="Name contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="commissionersBuilderName" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Reference contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="commissionersBuilderReference" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Email contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="commissionersBuilderEmail" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Status" class="w-full">
                        <cll-select
                            [options]="statusDataSource"
                            [formControl]="commissionersBuilderStatusControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Search (q)" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="commissionersBuilderQuery" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Columns" class="w-full">
                        <cll-multi-select
                            [options]="commissionersBuilderColumnDataSource"
                            [formControl]="commissionersBuilderColumnsControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort field" class="w-full">
                        <cll-select
                            [options]="commissionersBuilderColumnDataSource"
                            [formControl]="commissionersBuilderSortControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort direction" class="w-full">
                        <cll-select
                            [options]="sortDirectionDataSource"
                            [formControl]="commissionersBuilderSortDirectionControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Per page (1-500)" class="w-full">
                        <input cllFormInput type="number" [(ngModel)]="commissionersBuilderPerPage" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>
                </div>

                <div class="url-builder-includes">
                    <app-toggle
                        [checked]="commissionersBuilderIncludeAddress"
                        (toggled)="commissionersBuilderIncludeAddress = $event"
                        label="Include address" />
                    <app-toggle
                        [checked]="commissionersBuilderIncludeUsers"
                        (toggled)="commissionersBuilderIncludeUsers = $event"
                        label="Include users" />
                </div>

                <div class="url-builder-grid">
                    @if (commissionersBuilderIncludeAddress) {
                        <cll-form-field label="Address columns" class="w-full">
                            <cll-multi-select
                                [options]="commissionersBuilderAddressColumnDataSource"
                                [formControl]="commissionersBuilderAddressColumnsControl"
                            />
                        </cll-form-field>
                    }

                    @if (commissionersBuilderIncludeUsers) {
                        <cll-form-field label="Users columns" class="w-full">
                            <cll-multi-select
                                [options]="commissionersBuilderUsersColumnDataSource"
                                [formControl]="commissionersBuilderUsersColumnsControl"
                            />
                        </cll-form-field>
                    }
                </div>

                <cll-form-field label="Generated URL" class="w-full">
                    <textarea cllFormInput [value]="commissionersUrl" rows="3" readonly></textarea>
                </cll-form-field>

                @if (previewResults['commissioners']; as result) {
                    <div class="api-preview">
                        @if (result.loading) {
                            <span class="api-preview-loading">Running…</span>
                        } @else {
                            <div class="api-preview-meta">
                                <span class="api-preview-status" [class.ok]="(result.status ?? 0) < 400">HTTP {{ result.status }}</span>
                                <span class="api-preview-duration">{{ result.duration }}ms</span>
                            </div>
                            @if (result.error) {
                                <div class="api-preview-error">{{ result.error }}</div>
                            } @else {
                                <pre class="api-preview-body">{{ result.body }}</pre>
                            }
                        }
                    </div>
                }

                <cll-card-footer>
                    <button cll-outline-button type="button" (click)="copyCommissionersUrl()">
                        <cll-icon cllButtonIcon type="outlined">content_copy</cll-icon>
                        Copy URL
                    </button>
                    <button cll-outline-button type="button" (click)="runPreview('commissioners', commissionersUrl)" [disabled]="previewResults['commissioners']?.loading">
                        <cll-icon cllButtonIcon type="outlined">play_arrow</cll-icon>
                        Preview
                    </button>
                </cll-card-footer>
            </cll-collapsible-card>

            <cll-collapsible-card>
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">person_search</cll-icon>
                    <cll-card-title>Clients URL builder</cll-card-title>
                </cll-card-header>

                <div class="panel-text-sub-header">
                    Build a clients API URL with filters, includes, and selected columns.
                </div>

                <div class="url-builder-grid">
                    <cll-form-field label="Reference contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="clientsBuilderReference" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Email contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="clientsBuilderEmail" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="NHS number contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="clientsBuilderNhsNumber" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Status" class="w-full">
                        <cll-select
                            [options]="statusDataSource"
                            [formControl]="clientsBuilderStatusControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Search (q)" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="clientsBuilderQuery" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Associated commissioner ID" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="clientsBuilderCommissionerId" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Columns" class="w-full">
                        <cll-multi-select
                            [options]="clientsBuilderColumnDataSource"
                            [formControl]="clientsBuilderColumnsControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort field" class="w-full">
                        <cll-select
                            [options]="clientsBuilderColumnDataSource"
                            [formControl]="clientsBuilderSortControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort direction" class="w-full">
                        <cll-select
                            [options]="sortDirectionDataSource"
                            [formControl]="clientsBuilderSortDirectionControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Per page (1-500)" class="w-full">
                        <input cllFormInput type="number" [(ngModel)]="clientsBuilderPerPage" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>
                </div>

                <div class="url-builder-includes">
                    <app-toggle
                        [checked]="clientsBuilderIncludePerson"
                        (toggled)="clientsBuilderIncludePerson = $event"
                        label="Include person" />
                    <app-toggle
                        [checked]="clientsBuilderIncludePersonAddress"
                        (toggled)="clientsBuilderIncludePersonAddress = $event"
                        label="Include person address" />
                    <app-toggle
                        [checked]="clientsBuilderIncludeAssociatedCommissioners"
                        (toggled)="clientsBuilderIncludeAssociatedCommissioners = $event"
                        label="Include associated commissioners" />
                </div>

                <div class="url-builder-grid">
                    @if (clientsBuilderIncludePerson) {
                        <cll-form-field label="Person columns" class="w-full">
                            <cll-multi-select
                                [options]="clientsBuilderPersonColumnDataSource"
                                [formControl]="clientsBuilderPersonColumnsControl"
                            />
                        </cll-form-field>
                    }

                    @if (clientsBuilderIncludePersonAddress) {
                        <cll-form-field label="Address columns" class="w-full">
                            <cll-multi-select
                                [options]="clientsBuilderAddressColumnDataSource"
                                [formControl]="clientsBuilderAddressColumnsControl"
                            />
                        </cll-form-field>
                    }

                    @if (clientsBuilderIncludeAssociatedCommissioners) {
                        <cll-form-field label="Associated commissioner columns" class="w-full">
                            <cll-multi-select
                                [options]="clientsBuilderCommissionersColumnDataSource"
                                [formControl]="clientsBuilderCommissionersColumnsControl"
                            />
                        </cll-form-field>
                    }
                </div>

                <cll-form-field label="Generated URL" class="w-full">
                    <textarea cllFormInput [value]="clientsUrl" rows="3" readonly></textarea>
                </cll-form-field>

                @if (previewResults['clients']; as result) {
                    <div class="api-preview">
                        @if (result.loading) {
                            <span class="api-preview-loading">Running…</span>
                        } @else {
                            <div class="api-preview-meta">
                                <span class="api-preview-status" [class.ok]="(result.status ?? 0) < 400">HTTP {{ result.status }}</span>
                                <span class="api-preview-duration">{{ result.duration }}ms</span>
                            </div>
                            @if (result.error) {
                                <div class="api-preview-error">{{ result.error }}</div>
                            } @else {
                                <pre class="api-preview-body">{{ result.body }}</pre>
                            }
                        }
                    </div>
                }

                <cll-card-footer>
                    <button cll-outline-button type="button" (click)="copyClientsUrl()">
                        <cll-icon cllButtonIcon type="outlined">content_copy</cll-icon>
                        Copy URL
                    </button>
                    <button cll-outline-button type="button" (click)="runPreview('clients', clientsUrl)" [disabled]="previewResults['clients']?.loading">
                        <cll-icon cllButtonIcon type="outlined">play_arrow</cll-icon>
                        Preview
                    </button>
                </cll-card-footer>
            </cll-collapsible-card>

            <cll-collapsible-card>
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">map</cll-icon>
                    <cll-card-title>Regions URL builder</cll-card-title>
                </cll-card-header>

                <div class="panel-text-sub-header">
                    Build a regions API URL with filters, sort, and selected column options. Priorities and zones are always included.
                </div>

                <div class="url-builder-grid">
                    <cll-form-field label="Name contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="regionsBuilderName" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Reference contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="regionsBuilderReference" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Status" class="w-full">
                        <cll-select
                            [options]="statusDataSource"
                            [formControl]="regionsBuilderStatusControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Search (q)" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="regionsBuilderQuery" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Columns" class="w-full">
                        <cll-multi-select
                            [options]="regionsBuilderColumnDataSource"
                            [formControl]="regionsBuilderColumnsControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Priority columns" class="w-full">
                        <cll-multi-select
                            [options]="regionsBuilderPriorityColumnDataSource"
                            [formControl]="regionsBuilderPriorityColumnsControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Zone columns" class="w-full">
                        <cll-multi-select
                            [options]="regionsBuilderZoneColumnDataSource"
                            [formControl]="regionsBuilderZoneColumnsControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort field" class="w-full">
                        <cll-select
                            [options]="regionsBuilderColumnDataSource"
                            [formControl]="regionsBuilderSortControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort direction" class="w-full">
                        <cll-select
                            [options]="sortDirectionDataSource"
                            [formControl]="regionsBuilderSortDirectionControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Per page (1-500)" class="w-full">
                        <input cllFormInput type="number" [(ngModel)]="regionsBuilderPerPage" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>
                </div>

                <div class="url-builder-includes">
                    <app-toggle
                        [checked]="regionsBuilderIncludeProviders"
                        (toggled)="regionsBuilderIncludeProviders = $event"
                        label="Include providers" />
                </div>

                <div class="url-builder-grid">
                    @if (regionsBuilderIncludeProviders) {
                        <cll-form-field label="Provider columns" class="w-full">
                            <cll-multi-select
                                [options]="regionsBuilderProviderColumnDataSource"
                                [formControl]="regionsBuilderProviderColumnsControl"
                            />
                        </cll-form-field>
                    }
                </div>

                <cll-form-field label="Generated URL" class="w-full">
                    <textarea cllFormInput [value]="regionsUrl" rows="3" readonly></textarea>
                </cll-form-field>

                @if (previewResults['regions']; as result) {
                    <div class="api-preview">
                        @if (result.loading) {
                            <span class="api-preview-loading">Running…</span>
                        } @else {
                            <div class="api-preview-meta">
                                <span class="api-preview-status" [class.ok]="(result.status ?? 0) < 400">HTTP {{ result.status }}</span>
                                <span class="api-preview-duration">{{ result.duration }}ms</span>
                            </div>
                            @if (result.error) {
                                <div class="api-preview-error">{{ result.error }}</div>
                            } @else {
                                <pre class="api-preview-body">{{ result.body }}</pre>
                            }
                        }
                    </div>
                }

                <cll-card-footer>
                    <button cll-outline-button type="button" (click)="copyRegionsUrl()">
                        <cll-icon cllButtonIcon type="outlined">content_copy</cll-icon>
                        Copy URL
                    </button>
                    <button cll-outline-button type="button" (click)="runPreview('regions', regionsUrl)" [disabled]="previewResults['regions']?.loading">
                        <cll-icon cllButtonIcon type="outlined">play_arrow</cll-icon>
                        Preview
                    </button>
                </cll-card-footer>
            </cll-collapsible-card>

            <cll-collapsible-card>
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">interests</cll-icon>
                    <cll-card-title>Care types URL builder</cll-card-title>
                </cll-card-header>

                <div class="panel-text-sub-header">
                    Build a care types API URL with filters, optional care options include, and selected column options.
                </div>

                <div class="url-builder-grid">
                    <cll-form-field label="Status" class="w-full">
                        <cll-select
                            [options]="statusDataSource"
                            [formControl]="careTypesBuilderStatusControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Search (q)" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="careTypesBuilderQuery" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Columns" class="w-full">
                        <cll-multi-select
                            [options]="careTypesBuilderColumnDataSource"
                            [formControl]="careTypesBuilderColumnsControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort field" class="w-full">
                        <cll-select
                            [options]="careTypesBuilderColumnDataSource"
                            [formControl]="careTypesBuilderSortControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort direction" class="w-full">
                        <cll-select
                            [options]="sortDirectionDataSource"
                            [formControl]="careTypesBuilderSortDirectionControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Per page (1-500)" class="w-full">
                        <input cllFormInput type="number" [(ngModel)]="careTypesBuilderPerPage" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>
                </div>

                <div class="url-builder-includes">
                    <app-toggle
                        [checked]="careTypesBuilderIncludeCareOptions"
                        (toggled)="careTypesBuilderIncludeCareOptions = $event"
                        label="Include care options" />
                </div>

                <div class="url-builder-grid">
                    @if (careTypesBuilderIncludeCareOptions) {
                        <cll-form-field label="Care option columns" class="w-full">
                            <cll-multi-select
                                [options]="careTypesBuilderCareOptionColumnDataSource"
                                [formControl]="careTypesBuilderCareOptionColumnsControl"
                            />
                        </cll-form-field>
                    }
                </div>

                <cll-form-field label="Generated URL" class="w-full">
                    <textarea cllFormInput [value]="careTypesUrl" rows="3" readonly></textarea>
                </cll-form-field>

                @if (previewResults['care-types']; as result) {
                    <div class="api-preview">
                        @if (result.loading) {
                            <span class="api-preview-loading">Running…</span>
                        } @else {
                            <div class="api-preview-meta">
                                <span class="api-preview-status" [class.ok]="(result.status ?? 0) < 400">HTTP {{ result.status }}</span>
                                <span class="api-preview-duration">{{ result.duration }}ms</span>
                            </div>
                            @if (result.error) {
                                <div class="api-preview-error">{{ result.error }}</div>
                            } @else {
                                <pre class="api-preview-body">{{ result.body }}</pre>
                            }
                        }
                    </div>
                }

                <cll-card-footer>
                    <button cll-outline-button type="button" (click)="copyCareTypesUrl()">
                        <cll-icon cllButtonIcon type="outlined">content_copy</cll-icon>
                        Copy URL
                    </button>
                    <button cll-outline-button type="button" (click)="runPreview('care-types', careTypesUrl)" [disabled]="previewResults['care-types']?.loading">
                        <cll-icon cllButtonIcon type="outlined">play_arrow</cll-icon>
                        Preview
                    </button>
                </cll-card-footer>
            </cll-collapsible-card>

            <cll-collapsible-card>
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">category</cll-icon>
                    <cll-card-title>Care options URL builder</cll-card-title>
                </cll-card-header>

                <div class="panel-text-sub-header">
                    Build a care options API URL with filters, sort, and selected column options.
                </div>

                <div class="url-builder-grid">
                    <cll-form-field label="Care type ID" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="careOptionsBuilderCareTypeId" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Status" class="w-full">
                        <cll-select
                            [options]="statusDataSource"
                            [formControl]="careOptionsBuilderStatusControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Include price" class="w-full">
                        <cll-select
                            [options]="careOptionsBuilderIncludePriceDataSource"
                            [formControl]="careOptionsBuilderIncludePriceControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Search (q)" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="careOptionsBuilderQuery" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Columns" class="w-full">
                        <cll-multi-select
                            [options]="careOptionsBuilderColumnDataSource"
                            [formControl]="careOptionsBuilderColumnsControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Care type columns" class="w-full">
                        <cll-multi-select
                            [options]="careOptionsBuilderCareTypeColumnDataSource"
                            [formControl]="careOptionsBuilderCareTypeColumnsControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort field" class="w-full">
                        <cll-select
                            [options]="careOptionsBuilderColumnDataSource"
                            [formControl]="careOptionsBuilderSortControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort direction" class="w-full">
                        <cll-select
                            [options]="sortDirectionDataSource"
                            [formControl]="careOptionsBuilderSortDirectionControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Per page (1-500)" class="w-full">
                        <input cllFormInput type="number" [(ngModel)]="careOptionsBuilderPerPage" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>
                </div>

                <cll-form-field label="Generated URL" class="w-full">
                    <textarea cllFormInput [value]="careOptionsUrl" rows="3" readonly></textarea>
                </cll-form-field>

                @if (previewResults['care-options']; as result) {
                    <div class="api-preview">
                        @if (result.loading) {
                            <span class="api-preview-loading">Running…</span>
                        } @else {
                            <div class="api-preview-meta">
                                <span class="api-preview-status" [class.ok]="(result.status ?? 0) < 400">HTTP {{ result.status }}</span>
                                <span class="api-preview-duration">{{ result.duration }}ms</span>
                            </div>
                            @if (result.error) {
                                <div class="api-preview-error">{{ result.error }}</div>
                            } @else {
                                <pre class="api-preview-body">{{ result.body }}</pre>
                            }
                        }
                    </div>
                }

                <cll-card-footer>
                    <button cll-outline-button type="button" (click)="copyCareOptionsUrl()">
                        <cll-icon cllButtonIcon type="outlined">content_copy</cll-icon>
                        Copy URL
                    </button>
                    <button cll-outline-button type="button" (click)="runPreview('care-options', careOptionsUrl)" [disabled]="previewResults['care-options']?.loading">
                        <cll-icon cllButtonIcon type="outlined">play_arrow</cll-icon>
                        Preview
                    </button>
                </cll-card-footer>
            </cll-collapsible-card>

            <cll-collapsible-card>
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">undo</cll-icon>
                    <cll-card-title>Handbacks URL builder</cll-card-title>
                </cll-card-header>

                <div class="panel-text-sub-header">
                    Build a handbacks API URL with filters, optional package include, and selected column options.
                </div>

                <div class="url-builder-grid">
                    <cll-form-field label="Package ID" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="handbacksBuilderPackageId" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Provider ID" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="handbacksBuilderProviderId" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Handback reason ID" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="handbacksBuilderHandbackReasonId" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Private" class="w-full">
                        <cll-select
                            [options]="handbacksBuilderPrivateDataSource"
                            [formControl]="handbacksBuilderPrivateControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Search (q)" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="handbacksBuilderQuery" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Columns" class="w-full">
                        <cll-multi-select
                            [options]="handbacksBuilderColumnDataSource"
                            [formControl]="handbacksBuilderColumnsControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort field" class="w-full">
                        <cll-select
                            [options]="handbacksBuilderColumnDataSource"
                            [formControl]="handbacksBuilderSortControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort direction" class="w-full">
                        <cll-select
                            [options]="sortDirectionDataSource"
                            [formControl]="handbacksBuilderSortDirectionControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Per page (1-500)" class="w-full">
                        <input cllFormInput type="number" [(ngModel)]="handbacksBuilderPerPage" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>
                </div>

                <div class="url-builder-includes">
                    <app-toggle
                        [checked]="handbacksBuilderIncludePackage"
                        (toggled)="handbacksBuilderIncludePackage = $event"
                        label="Include package" />
                </div>

                <div class="url-builder-grid">
                    @if (handbacksBuilderIncludePackage) {
                        <cll-form-field label="Package columns" class="w-full">
                            <cll-multi-select
                                [options]="handbacksBuilderPackageColumnDataSource"
                                [formControl]="handbacksBuilderPackageColumnsControl"
                            />
                        </cll-form-field>
                    }
                </div>

                <cll-form-field label="Generated URL" class="w-full">
                    <textarea cllFormInput [value]="handbacksUrl" rows="4" readonly></textarea>
                </cll-form-field>

                @if (previewResults['handbacks']; as result) {
                    <div class="api-preview">
                        @if (result.loading) {
                            <span class="api-preview-loading">Running…</span>
                        } @else {
                            <div class="api-preview-meta">
                                <span class="api-preview-status" [class.ok]="(result.status ?? 0) < 400">HTTP {{ result.status }}</span>
                                <span class="api-preview-duration">{{ result.duration }}ms</span>
                            </div>
                            @if (result.error) {
                                <div class="api-preview-error">{{ result.error }}</div>
                            } @else {
                                <pre class="api-preview-body">{{ result.body }}</pre>
                            }
                        }
                    </div>
                }

                <cll-card-footer>
                    <button cll-outline-button type="button" (click)="copyHandbacksUrl()">
                        <cll-icon cllButtonIcon type="outlined">content_copy</cll-icon>
                        Copy URL
                    </button>
                    <button cll-outline-button type="button" (click)="runPreview('handbacks', handbacksUrl)" [disabled]="previewResults['handbacks']?.loading">
                        <cll-icon cllButtonIcon type="outlined">play_arrow</cll-icon>
                        Preview
                    </button>
                </cll-card-footer>
            </cll-collapsible-card>

            <cll-collapsible-card>
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">forum</cll-icon>
                    <cll-card-title>Message threads URL builder</cll-card-title>
                </cll-card-header>

                <div class="panel-text-sub-header">
                    Build a message threads API URL with filters and column options for threads, senders, and messages.
                </div>

                <div class="url-builder-grid">
                    <cll-form-field label="Sender ID" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="messageThreadsBuilderSenderId" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Subject contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="messageThreadsBuilderSubject" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Columns" class="w-full">
                        <cll-multi-select
                            [options]="messageThreadsBuilderColumnDataSource"
                            [formControl]="messageThreadsBuilderColumnsControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sender columns" class="w-full">
                        <cll-multi-select
                            [options]="messageThreadsBuilderSenderColumnDataSource"
                            [formControl]="messageThreadsBuilderSenderColumnsControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Message columns" class="w-full">
                        <cll-multi-select
                            [options]="messageThreadsBuilderMessageColumnDataSource"
                            [formControl]="messageThreadsBuilderMessageColumnsControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Message sender columns" class="w-full">
                        <cll-multi-select
                            [options]="messageThreadsBuilderMessageSenderColumnDataSource"
                            [formControl]="messageThreadsBuilderMessageSenderColumnsControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort field" class="w-full">
                        <cll-select
                            [options]="messageThreadsBuilderColumnDataSource"
                            [formControl]="messageThreadsBuilderSortControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort direction" class="w-full">
                        <cll-select
                            [options]="sortDirectionDataSource"
                            [formControl]="messageThreadsBuilderSortDirectionControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Per page (1-500)" class="w-full">
                        <input cllFormInput type="number" [(ngModel)]="messageThreadsBuilderPerPage" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>
                </div>

                <cll-form-field label="Generated URL" class="w-full">
                    <textarea cllFormInput [value]="messageThreadsUrl" rows="4" readonly></textarea>
                </cll-form-field>

                @if (previewResults['message-threads']; as result) {
                    <div class="api-preview">
                        @if (result.loading) {
                            <span class="api-preview-loading">Running…</span>
                        } @else {
                            <div class="api-preview-meta">
                                <span class="api-preview-status" [class.ok]="(result.status ?? 0) < 400">HTTP {{ result.status }}</span>
                                <span class="api-preview-duration">{{ result.duration }}ms</span>
                            </div>
                            @if (result.error) {
                                <div class="api-preview-error">{{ result.error }}</div>
                            } @else {
                                <pre class="api-preview-body">{{ result.body }}</pre>
                            }
                        }
                    </div>
                }

                <cll-card-footer>
                    <button cll-outline-button type="button" (click)="copyMessageThreadsUrl()">
                        <cll-icon cllButtonIcon type="outlined">content_copy</cll-icon>
                        Copy URL
                    </button>
                    <button cll-outline-button type="button" (click)="runPreview('message-threads', messageThreadsUrl)" [disabled]="previewResults['message-threads']?.loading">
                        <cll-icon cllButtonIcon type="outlined">play_arrow</cll-icon>
                        Preview
                    </button>
                </cll-card-footer>
            </cll-collapsible-card>

            <cll-collapsible-card>
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">medical_services</cll-icon>
                    <cll-card-title>Packages URL builder</cll-card-title>
                </cll-card-header>

                <div class="panel-text-sub-header">
                    Build a packages API URL with filters, includes, sort, and selected column options.
                </div>

                <div class="url-builder-grid">
                    <cll-form-field label="Reference contains" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="packagesBuilderReference" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Commissioner ID" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="packagesBuilderCommissionerId" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Provider ID" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="packagesBuilderProviderId" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Care option ID" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="packagesBuilderCareOptionId" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Open" class="w-full">
                        <cll-select
                            [options]="packagesBuilderOpenDataSource"
                            [formControl]="packagesBuilderOpenControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Published" class="w-full">
                        <cll-select
                            [options]="packagesBuilderPublishDataSource"
                            [formControl]="packagesBuilderPublishControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Search (q)" class="w-full">
                        <input cllFormInput type="text" [(ngModel)]="packagesBuilderQuery" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>

                    <cll-form-field label="Columns" class="w-full">
                        <cll-multi-select
                            [options]="packagesBuilderColumnDataSource"
                            [formControl]="packagesBuilderColumnsControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort field" class="w-full">
                        <cll-select
                            [options]="packagesBuilderColumnDataSource"
                            [formControl]="packagesBuilderSortControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Sort direction" class="w-full">
                        <cll-select
                            [options]="sortDirectionDataSource"
                            [formControl]="packagesBuilderSortDirectionControl"
                        />
                    </cll-form-field>

                    <cll-form-field label="Per page (1-500)" class="w-full">
                        <input cllFormInput type="number" [(ngModel)]="packagesBuilderPerPage" [ngModelOptions]="{standalone: true}">
                    </cll-form-field>
                </div>

                <div class="url-builder-includes">
                    <app-toggle
                        [checked]="packagesBuilderIncludeAddress"
                        (toggled)="packagesBuilderIncludeAddress = $event"
                        label="Include address" />
                    <app-toggle
                        [checked]="packagesBuilderIncludeClients"
                        (toggled)="packagesBuilderIncludeClients = $event"
                        label="Include clients" />
                    <app-toggle
                        [checked]="packagesBuilderIncludeCommissioner"
                        (toggled)="packagesBuilderIncludeCommissioner = $event"
                        label="Include commissioner" />
                    <app-toggle
                        [checked]="packagesBuilderIncludeProvider"
                        (toggled)="packagesBuilderIncludeProvider = $event"
                        label="Include provider" />
                    <app-toggle
                        [checked]="packagesBuilderIncludeCareOption"
                        (toggled)="packagesBuilderIncludeCareOption = $event"
                        label="Include care option" />
                </div>

                <div class="url-builder-grid">
                    @if (packagesBuilderIncludeAddress) {
                        <cll-form-field label="Address columns" class="w-full">
                            <cll-multi-select
                                [options]="packagesBuilderAddressColumnDataSource"
                                [formControl]="packagesBuilderAddressColumnsControl"
                            />
                        </cll-form-field>
                    }

                    @if (packagesBuilderIncludeClients) {
                        <cll-form-field label="Client columns" class="w-full">
                            <cll-multi-select
                                [options]="packagesBuilderClientsColumnDataSource"
                                [formControl]="packagesBuilderClientsColumnsControl"
                            />
                        </cll-form-field>
                    }

                    @if (packagesBuilderIncludeCommissioner) {
                        <cll-form-field label="Commissioner columns" class="w-full">
                            <cll-multi-select
                                [options]="packagesBuilderCommissionerColumnDataSource"
                                [formControl]="packagesBuilderCommissionerColumnsControl"
                            />
                        </cll-form-field>
                    }

                    @if (packagesBuilderIncludeProvider) {
                        <cll-form-field label="Provider columns" class="w-full">
                            <cll-multi-select
                                [options]="packagesBuilderProviderColumnDataSource"
                                [formControl]="packagesBuilderProviderColumnsControl"
                            />
                        </cll-form-field>
                    }

                    @if (packagesBuilderIncludeCareOption) {
                        <cll-form-field label="Care option columns" class="w-full">
                            <cll-multi-select
                                [options]="packagesBuilderCareOptionColumnDataSource"
                                [formControl]="packagesBuilderCareOptionColumnsControl"
                            />
                        </cll-form-field>
                    }
                </div>

                <cll-form-field label="Generated URL" class="w-full">
                    <textarea cllFormInput [value]="packagesUrl" rows="4" readonly></textarea>
                </cll-form-field>

                @if (previewResults['packages']; as result) {
                    <div class="api-preview">
                        @if (result.loading) {
                            <span class="api-preview-loading">Running…</span>
                        } @else {
                            <div class="api-preview-meta">
                                <span class="api-preview-status" [class.ok]="(result.status ?? 0) < 400">HTTP {{ result.status }}</span>
                                <span class="api-preview-duration">{{ result.duration }}ms</span>
                            </div>
                            @if (result.error) {
                                <div class="api-preview-error">{{ result.error }}</div>
                            } @else {
                                <pre class="api-preview-body">{{ result.body }}</pre>
                            }
                        }
                    </div>
                }

                <cll-card-footer>
                    <button cll-outline-button type="button" (click)="copyPackagesUrl()">
                        <cll-icon cllButtonIcon type="outlined">content_copy</cll-icon>
                        Copy URL
                    </button>
                    <button cll-outline-button type="button" (click)="runPreview('packages', packagesUrl)" [disabled]="previewResults['packages']?.loading">
                        <cll-icon cllButtonIcon type="outlined">play_arrow</cll-icon>
                        Preview
                    </button>
                </cll-card-footer>
            </cll-collapsible-card>

            @if (createdToken; as token) {
                <cll-card appearance="outlined">
                    <cll-card-header>
                        <cll-icon cllCardIcon type="outlined">key</cll-icon>
                        <cll-card-title>New token</cll-card-title>
                    </cll-card-header>

                    <div class="panel-text-sub-header">
                        Copy this token now. For security reasons it is only shown once.
                    </div>

                    <cll-form-field label="Token" class="w-full">
                        <textarea cllFormInput [value]="token" rows="4" readonly></textarea>
                    </cll-form-field>

                    <cll-card-footer>
                        <button cll-outline-button type="button" (click)="copyToken(token)">
                            <cll-icon cllButtonIcon type="outlined">content_copy</cll-icon>
                            Copy token
                        </button>
                    </cll-card-footer>
                </cll-card>
            }
        </div>
    </div>
</form>
